import React from 'react';
import { mapKeys, camelCase } from 'lodash';
import { arrayOf, shape, string, number, object } from 'prop-types';
import { logger } from '@fiverr-private/obs';
import SessionGlobalMessages from '@fiverr-private/session_global_messages';
import cookie from '../../lib/cookie';
import { DISCOVER_FIVERR_TRANSLATION_PREFIX } from './DiscoverFiverr/constants';
import { STORED_MESSAGES_TYPES } from './constants';
import CookieApproval from './CookieApproval';
import DiscoverFiverr, { show as showDiscoverFiverr } from './DiscoverFiverr';
import UserActivation, { show as showUserActivation } from './UserActivation';
import RealtimeMessages, { serializer as realtimeMessagesSerializer } from './RealtimeMessages';

/**
 * Translates a given text and locale with fiverr discover prefix
 * @param {String} text The scoped text key to translate.
 * @param {String} locale The locale to use.
 * @return {String}
 */
const translateDiscoverFiverrText = (text, locale) => i18n.t(
    `${DISCOVER_FIVERR_TRANSLATION_PREFIX}.${text}`,
    { $scope: locale }
);

/**
 * Serializes the layout data into a format accepted by this component.
 * @param {Context} options.context The current request's context.
 * @param {String[]} options.locales The current request's user data.
 * @param {User} options.user The current request's user data.
 * @param {StoredGlobalMessage[]} options.storedGlobalMessages A list of current user stored global messages.
 * @return {Object}
 */
export const serializer = ({
    context,
    locales,
    user = {},
    storedGlobalMessages = []
}) => ({
    context,
    locales,
    user,
    discoverFiverr: {
        intro: translateDiscoverFiverrText('intro', context.browserLanguage),
        letsGo: translateDiscoverFiverrText('lets_go', context.browserLanguage)
    },
    storedMessages: storedGlobalMessages.map(({ id, content_type, ...rest }) => ({
        id,
        type: content_type,
        ...mapKeys(rest, (_, key) => camelCase(key))
    }))
});

const StoredGlobalMessages = ({ storedMessages = [], userId }) => (
    storedMessages.map(({ type, id, ...rest }) => {
        const props = {
            key: id,
            id,
            ...rest
        };

        switch (type) {
            case STORED_MESSAGES_TYPES.COOKIE_APPROVAL:
                return <CookieApproval {...props}/>;
            default:
                logger.warn(`Unfamiliar global message type: ${type} for user: ${userId}.`);
                return null;
        }
    })
);

const ContextualGlobalMessages = ({ context, locales, user, discoverFiverr, cookies }) => (
    <>
        {showDiscoverFiverr({
            user,
            context,
            locales,
            cookies
        }) && <DiscoverFiverr browserLanguage={context.browserLanguage}
            url={context.url}
            introText={discoverFiverr.intro}
            letsGoText={discoverFiverr.letsGo}/>
        }
        {showUserActivation({
            user,
            context
        }) && <UserActivation user={user}/>}
    </>
);

const GlobalMessages = ({ context, locales, storedMessages = [], user, discoverFiverr, headerCookies }) => (
    <>
        <SessionGlobalMessages messages={context.messages}/>
        <StoredGlobalMessages storedMessages={storedMessages} userId={user.id}/>
        <ContextualGlobalMessages user={user}
            cookies={cookie.getAll(headerCookies)}
            context={context}
            discoverFiverr={discoverFiverr}
            locales={locales}/>
        { !!user.id && <RealtimeMessages {...realtimeMessagesSerializer({ user })}/> }
    </>
);

StoredGlobalMessages.propTypes = {
    storedMessages: arrayOf(shape({
        id: string,
        type: string
    })),
    userId: number
};

ContextualGlobalMessages.propTypes = {
    user: object,
    cookies: object,
    context: object,
    discoverFiverr: shape({
        intro: string.isRequired,
        letsGo: string.isRequired
    }),
    locales: arrayOf(object)
};

GlobalMessages.propTypes = {
    ...ContextualGlobalMessages.propTypes,
    ...StoredGlobalMessages.propTypes,
    headerCookies: string
};

export default GlobalMessages;
